import {
    Component,
    forwardRef,
    Input,
    OnInit,
    ChangeDetectionStrategy
} from "@angular/core";
import { NG_VALUE_ACCESSOR, FormsModule } from "@angular/forms";
import { pullAll } from "lodash";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { BlockComponent } from "../container-block/block.component";
import { FilterPipe } from "../../pipes/filter.pipe";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiRangeSelectComponent)
        }
    ],
    selector: "rask-multi-range-select",
    templateUrl: "./multi-range-select.component.html",
    styleUrls: ["./multi-range-select.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        FilterPipe
    ]
})
export class MultiRangeSelectComponent extends ControlValueAccessorBaseComponent implements OnInit {
    @Input() items: any[];
    @Input() itemsTitle: string;
    @Input() valueTitle: string;
    @Input() orderItemsBy: (a: any[]) => void;
    @Input() orderValueBy: (a: any[]) => void;
    @Input() viewFormatter: (item: any) => string;
    @Input() size = 20;
    @Input() allowValueSort = false;

    selectedFromItems: any[];
    selectedFromValue: any[];
    itemsSearchText: string;
    valueSearchText: string;

    constructor() {
        super();

        this.selectedFromItems = [];
        this.selectedFromValue = [];
    }

    ngOnInit() {
        super.ngOnInit();
        super.writeValue(this.value);
    }

    setValue(val: any) {
        this.value = val;
    }

    addToTarget = () => {
        if (this.selectedFromItems.length > 0) {
            this.switchArray(this.items, this.value, this.selectedFromItems);
            this.resetSelection();
            if (this.orderValueBy) {
                this.orderValueBy(this.value);
            }
            this.setValue(this.value);
        }
    };

    removeFromTarget = () => {
        if (this.selectedFromValue.length > 0) {
            this.switchArray(this.value, this.items, this.selectedFromValue);
            this.resetSelection();
            if (this.orderItemsBy) {
                this.orderItemsBy(this.items);
            }
            this.setValue(this.value);
        }
    };

    moveValuePartUp = () => {
        const startIndex = this.value.indexOf(this.selectedFromValue[0]);
        this.move(this.value, this.selectedFromValue.length, startIndex, startIndex - 1);
        this.setValue(this.value);
    };

    moveValuePartDown = () => {
        const startIndex = this.value.indexOf(this.selectedFromValue[0]);
        this.move(this.value, this.selectedFromValue.length, startIndex, startIndex + 1);
        this.setValue(this.value);
    };

    canMoveUp() {
        if (this.valueSearchText) {
            return false;
        }
        if (this.selectedFromValue && this.selectedFromValue.length) {
            const startIndex = this.value.indexOf(this.selectedFromValue[0]);
            if (startIndex > 0) {
                return true;
            }
        }
        return false;
    }
    canMoveDown() {
        if (this.valueSearchText) {
            return false;
        }
        if (this.selectedFromValue && this.selectedFromValue.length) {
            const lastIndex = this.value.indexOf(this.selectedFromValue[this.selectedFromValue.length - 1]);
            if (lastIndex < this.value.length - 1) {
                return true;
            }
        }
        return false;
    }

    preventEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * Flyttar en till många sekventiellt orienterade element i en array, från ett index till ett annat
     *
     * @param sourceArray Vilken array som ska påverkas
     * @param itemCountToMove Antalet element som ska flyttas
     * @param startIndex Index från där första elementet ska flyttas
     * @param destinationIndex Index dit alla element ska flyttas till
     */
    private readonly move = (sourceArray: any[], itemCountToMove: number, startIndex: number, destinationIndex: number) => {
        const removedItems = sourceArray.splice(startIndex, itemCountToMove);
        sourceArray.splice(destinationIndex, 0, ...removedItems);
    };

    private readonly switchArray = (source: any[], target: any[], newItems: any[]): void => {
        target.unshift(...newItems);
        pullAll(source, newItems);
    };

    private readonly resetSelection = () => {
        this.selectedFromItems = [];
        this.selectedFromValue = [];
    };
}
